import React, { useEffect, useMemo, useState } from "react";
import "./ai-loader.css";
import { ContentMode } from "../../types/creator-studio";

interface AILoaderProps {
  type: ContentMode;
  contextStage?: string;
}

const AILoader: React.FC<AILoaderProps> = ({ type, contextStage }) => {
  const stages = useMemo(
    () => [
      "Preparing your request",
      contextStage || "Reading brand knowledge",
      "Checking source context",
      "Generating content variations",
      "Polishing drafts",
    ],
    [contextStage]
  );
  const [activeStage, setActiveStage] = useState(0);
  const [showSlowMessage, setShowSlowMessage] = useState(false);

  useEffect(() => {
    setActiveStage(0);
    setShowSlowMessage(false);
  }, [stages]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveStage((current) => Math.min(current + 1, stages.length - 1));
    }, 4500);
    const slowTimer = window.setTimeout(() => {
      setShowSlowMessage(true);
    }, 45000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(slowTimer);
    };
  }, [stages.length]);

  return (
    <>
      <div className="ai-loader-container">
        <div className="ai-loader-content">
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div className="orb-ring"></div>
            <div className="orb-ring-2"></div>
            <div className="magic-orb">
              <div className="magic-icon">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                </svg>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: "40px" }}>
            <h3 className="loading-text">
              {type === ContentMode.SEO_ARTICLE
                ? "Generating SEO Article..."
                : type === ContentMode.SOCIAL
                ? "Generating Social Post..."
                : "Generating..."}
            </h3>
            <p className="loading-subtext">
              {type === ContentMode.SEO_ARTICLE
                ? "Our AI is analyzing market trends and crafting the perfect SEO article for your brand."
                : type === ContentMode.SOCIAL
                ? "Our AI is analyzing market trends and crafting the perfect social post for your brand."
                : "Our AI is analyzing market trends and crafting the perfect email template for your brand."}
            </p>
            <div className="ai-loader-steps" aria-label="Generation progress">
              {stages.map((stage, index) => (
                <div
                  className={`ai-loader-step ${
                    index < activeStage ? "complete" : ""
                  } ${index === activeStage ? "active" : ""}`}
                  key={`${stage}-${index}`}
                >
                  <span className="ai-loader-step-dot" />
                  <span>{stage}</span>
                </div>
              ))}
            </div>
            {showSlowMessage && (
              <p className="ai-loader-slow-note">
                Still working. Larger sources can take a little longer.
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AILoader;
